import React, { Component } from "react";

// ─── API helper ────────────────────────────────────────────────────────────
const api = {
  get: async (path) => {
    const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api${path}`);
    return res.json();
  },
  post: async (path, body) => {
    const res = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api${path}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );
    return res.json();
  },
};

// ─── Status helper ─────────────────────────────────────────────────────────
function getStatus(units, crit, low) {
  if (units <= crit) return "critical";
  if (units <= low) return "low";
  return "ok";
}

const STATUS = {
  critical: { bg: "#FEE2E2", text: "#991B1B", border: "#FECACA" },
  low: { bg: "#FEF3C7", text: "#92400E", border: "#FDE68A" },
  ok: { bg: "#D1FAE5", text: "#065F46", border: "#A7F3D0" },
};

const STATUS_LABEL = {
  critical: "Critical",
  low: "Low stock",
  ok: "Sufficient",
};

const ALL_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

// ─── Small building blocks ─────────────────────────────────────────────────
class Badge extends Component {
  render() {
    const { status } = this.props;
    const c = STATUS[status];
    return (
      <span
        style={{
          fontSize: 11,
          fontWeight: 600,
          padding: "4px 14px",
          borderRadius: 20,
          background: c.bg,
          color: c.text,
          border: `1px solid ${c.border}`,
          display: "inline-block",
        }}
      >
        {STATUS_LABEL[status]}
      </span>
    );
  }
}

class Btn extends Component {
  render() {
    const {
      children,
      variant = "primary",
      onClick,
      style: s,
      disabled,
    } = this.props;
    const variants = {
      primary: { background: "#C0392B", color: "#fff" },
      secondary: { background: "#F1F5F9", color: "#475569" },
      danger: { background: "#DC2626", color: "#fff" },
      success: { background: "#16A34A", color: "#fff" },
    };
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        style={{
          padding: "8px 18px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          border: "none",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          transition: "all 0.2s",
          ...variants[variant],
          ...s,
        }}
      >
        {children}
      </button>
    );
  }
}

// ─── Add / Edit Threshold Modal ─────────────────────────────────────────────
class StockModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      blood_group: props.editing ? props.editing.blood_group : ALL_GROUPS[0],
      critical_threshold: props.editing ? props.editing.critical_threshold : "",
      low_threshold: props.editing ? props.editing.low_threshold : "",
      applyToAll: false,
      saving: false,
      error: "",
    };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleChange(e) {
    const { name, value, type, checked } = e.target;
    this.setState({ [name]: type === "checkbox" ? checked : value, error: "" });
  }

  async handleSubmit() {
    const { blood_group, critical_threshold, low_threshold, applyToAll } =
      this.state;
    const { editing, existingGroups, onDone } = this.props;

    if (critical_threshold === "" || low_threshold === "") {
      this.setState({ error: "Both threshold values are required." });
      return;
    }
    if (Number(critical_threshold) > Number(low_threshold)) {
      this.setState({
        error: "Critical threshold should not exceed low threshold.",
      });
      return;
    }

    this.setState({ saving: true, error: "" });

    try {
      if (applyToAll) {
        for (const bg of ALL_GROUPS) {
          const payload = {
            blood_group: bg,
            critical_threshold: Number(critical_threshold),
            low_threshold: Number(low_threshold),
          };
          const isExisting = existingGroups.includes(bg);
          const res = isExisting
            ? await api.post(`/stock/update`, payload)
            : await api.post(`/stock/create`, payload);

          if (!res.status) {
            this.setState({
              saving: false,
              error: `Failed on ${bg}: ${res.message || "Unknown error"}`,
            });
            return;
          }
        }
      } else if (editing) {
        const res = await api.post(`/stock/update`, {
          blood_group: editing.blood_group,
          critical_threshold: Number(critical_threshold),
          low_threshold: Number(low_threshold),
        });
        if (!res.status) {
          this.setState({
            saving: false,
            error: res.message || "Failed to update.",
          });
          return;
        }
      } else {
        const res = await api.post(`/stock/create`, {
          blood_group,
          critical_threshold: Number(critical_threshold),
          low_threshold: Number(low_threshold),
        });
        if (!res.status) {
          this.setState({
            saving: false,
            error: res.message || "Failed to create.",
          });
          return;
        }
      }

      this.setState({ saving: false });
      onDone();
    } catch (e) {
      this.setState({
        saving: false,
        error: "Something went wrong. Please try again.",
      });
    }
  }

  render() {
    const {
      blood_group,
      critical_threshold,
      low_threshold,
      applyToAll,
      saving,
      error,
    } = this.state;
    const { editing, onClose } = this.props;

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            width: 420,
            maxWidth: "90%",
            padding: 24,
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
        >
          <h2
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#0F172A",
              margin: "0 0 16px",
            }}
          >
            {editing ? `Edit MSQ — ${editing.blood_group}` : "Add MSQ"}
          </h2>
          {!editing && (
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 16,
                fontSize: 13,
                color: "#334155",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                name="applyToAll"
                checked={applyToAll}
                onChange={this.handleChange}
              />
              Apply these values to all blood groups
            </label>
          )}

          {!editing && !applyToAll && (
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Blood group</label>
              <select
                name="blood_group"
                value={blood_group}
                onChange={this.handleChange}
                style={inputStyle}
              >
                {ALL_GROUPS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Critical threshold</label>
            <input
              type="number"
              name="critical_threshold"
              value={critical_threshold}
              onChange={this.handleChange}
              style={inputStyle}
              placeholder="e.g. 5"
              min="0"
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Low threshold</label>
            <input
              type="number"
              name="low_threshold"
              value={low_threshold}
              onChange={this.handleChange}
              style={inputStyle}
              placeholder="e.g. 10"
              min="0"
            />
          </div>

          {error && (
            <div style={{ color: "#991B1B", fontSize: 13, marginBottom: 12 }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Btn variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Btn>
            <Btn onClick={this.handleSubmit} disabled={saving}>
              {saving ? "Saving…" : applyToAll ? "Apply to all" : "Save"}
            </Btn>
          </div>
        </div>
      </div>
    );
  }
}

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 600,
  color: "#475569",
  marginBottom: 6,
};

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  borderRadius: 8,
  border: "1px solid #E2E8F0",
  fontSize: 14,
  boxSizing: "border-box",
};

// ─── Stock Table ────────────────────────────────────────────────────────────
class StockList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      stock: [],
      loading: true,
      showModal: false,
      editing: null,
    };
    this.fetchStock = this.fetchStock.bind(this);
    this.openAddModal = this.openAddModal.bind(this);
    this.openEditModal = this.openEditModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.handleModalDone = this.handleModalDone.bind(this);
  }

  componentDidMount() {
    this.fetchStock();
  }

  async fetchStock() {
    this.setState({ loading: true });
    const res = await api.get(`/stock`);
    if (res.status) this.setState({ stock: res.data });
    this.setState({ loading: false });
  }

  openAddModal() {
    this.setState({ showModal: true, editing: null });
  }

  openEditModal(row) {
    this.setState({ showModal: true, editing: row });
  }

  closeModal() {
    this.setState({ showModal: false, editing: null });
  }

  handleModalDone() {
    this.setState({ showModal: false, editing: null });
    this.fetchStock();
  }

  render() {
    const { stock, loading, showModal, editing } = this.state;
    const existingGroups = stock.map((s) => s.blood_group);

    return (
      <div style={{ fontFamily: "'Inter', system-ui, sans-serif", padding: "24px", background: "#F8FAFC", minHeight: "100vh" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 24,
            }}
          >
            <div>
              <h1
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "#0F172A",
                  margin: 0,
                }}
              >
                MSQ List
              </h1>
              <p style={{ color: "#64748B", fontSize: 14, margin: "4px 0 0" }}>
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn onClick={this.openAddModal}>+ Add MSQ</Btn>
            </div>
          </div>

          {/* Table */}
          <div
            style={{
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #E2E8F0",
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
              }}
            >
              <thead>
                <tr style={{ background: "#F8FAFC" }}>
                  <th
                    style={{
                      padding: "14px 20px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #E2E8F0",
                      minWidth: "180px",
                    }}
                  >
                    BLOOD GROUP
                  </th>
                  <th
                    style={{
                      padding: "14px 20px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #E2E8F0",
                      minWidth: "120px",
                    }}
                  >
                    UNITS AVAILABLE
                  </th>
                  <th
                    style={{
                      padding: "14px 20px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #E2E8F0",
                      minWidth: "120px",
                    }}
                  >
                    CRITICAL THRESHOLD
                  </th>
                  <th
                    style={{
                      padding: "14px 20px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #E2E8F0",
                      minWidth: "100px",
                    }}
                  >
                    THRESHOLD
                  </th>
                  <th
                    style={{
                      padding: "14px 20px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #E2E8F0",
                      minWidth: "120px",
                    }}
                  >
                    STATUS
                  </th>
                  <th
                    style={{
                      padding: "14px 20px",
                      textAlign: "left",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      borderBottom: "2px solid #E2E8F0",
                      minWidth: "120px",
                    }}
                  >
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: "2.5rem",
                        textAlign: "center",
                        color: "#94A3B8",
                      }}
                    >
                      Loading stock...
                    </td>
                  </tr>
                ) : stock.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      style={{
                        padding: "2.5rem",
                        textAlign: "center",
                        color: "#94A3B8",
                      }}
                    >
                      No stock records found.
                    </td>
                  </tr>
                ) : (
                  stock.map((s) => {
                    const status = getStatus(
                      s.units_available,
                      s.critical_threshold,
                      s.low_threshold
                    );

                    return (
                      <tr key={s.blood_group} style={{ borderBottom: "1px solid #F1F5F9" }}>
                        {/* Blood Group */}
                        <td
                          style={{
                            padding: "16px 20px",
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div
                              style={{
                                width: 48,
                                height: 48,
                                background: "#FDECEC",
                                borderRadius: 12,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#B91C1C",
                                fontWeight: 700,
                                fontSize: 18,
                                flexShrink: 0,
                              }}
                            >
                              {s.blood_group}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#334155",
                                  marginBottom: 6,
                                }}
                              >
                                <span>Critical: <span style={{ color: "#DC2626", fontWeight: 600 }}>{s.critical_threshold}</span></span>
                                <span style={{ margin: "0 8px", color: "#CBD5E1" }}>|</span>
                                <span>Low: <span style={{ color: "#CA8A04", fontWeight: 600 }}>{s.low_threshold}</span></span>
                                <span style={{ margin: "0 8px", color: "#CBD5E1" }}>|</span>
                                <span>Current: <span style={{ color: "#2563EB", fontWeight: 600 }}>{s.units_available}</span></span>
                              </div>
                              <div
                                style={{
                                  height: 6,
                                  borderRadius: 10,
                                  overflow: "hidden",
                                  background: "#F1F5F9",
                                  position: "relative",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: `${Math.min(100, (s.units_available / (s.low_threshold + 20)) * 100)}%`,
                                    background: status === "critical" ? "#DC2626" : status === "low" ? "#CA8A04" : "#22C55E",
                                    borderRadius: 10,
                                    transition: "width 0.5s ease",
                                  }}
                                />
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Units Available */}
                        <td
                          style={{
                            padding: "16px 20px",
                            fontWeight: 600,
                            fontSize: 16,
                            color: "#0F172A",
                          }}
                        >
                          {s.units_available}
                        </td>

                        {/* Critical Threshold */}
                        <td
                          style={{
                            padding: "16px 20px",
                            fontSize: 14,
                            color: "#475569",
                          }}
                        >
                          {s.critical_threshold}
                        </td>

                        {/* Low Threshold */}
                        <td
                          style={{
                            padding: "16px 20px",
                            fontSize: 14,
                            color: "#475569",
                          }}
                        >
                          {s.low_threshold}
                        </td>

                        {/* Status */}
                        <td
                          style={{
                            padding: "16px 20px",
                          }}
                        >
                          <Badge status={status} />
                        </td>

                        {/* Actions */}
                        <td
                          style={{
                            padding: "16px 20px",
                          }}
                        >
                          <Btn
                            variant="secondary"
                            onClick={() => this.openEditModal(s)}
                            style={{ padding: "6px 16px", fontSize: 12 }}
                          >
                            Edit
                          </Btn>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {showModal && (
            <StockModal
              editing={editing}
              existingGroups={existingGroups}
              onClose={this.closeModal}
              onDone={this.handleModalDone}
            />
          )}
        </div>
      </div>
    );
  }
}

export default StockList;